import React, { useState, useEffect } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { useToast } from '../Toast';
import Icon from '../Icon';
import { C, S } from '../../utils/styles';

const filingLabels = {
  single: 'Single (S)',
  married: 'Married Filing Jointly (MFJ)',
  hoh: 'Head of Household (HoH)',
};

const filingNotes = {
  single: 'Also used by Married Filing Separately (MFS)',
  married: 'Also used by Qualifying Surviving Spouse (QSS)',
  hoh: '',
};

export default function FederalTaxEditor() {
  const { config, updateFederalBrackets } = useConfig();
  const addToast = useToast();
  const [activeFiling, setActiveFiling] = useState('single');
  const [draft, setDraft] = useState(JSON.parse(JSON.stringify(config.federalBrackets, (k, v) => v === Infinity ? '__INF__' : v)));
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setDraft(JSON.parse(JSON.stringify(config.federalBrackets, (k, v) => v === Infinity ? '__INF__' : v)));
    setHasChanges(false);
  }, [config.federalBrackets]);

  const brackets = draft[activeFiling] || [];

  const updateBracket = (idx, field, value) => {
    const updated = { ...draft };
    const arr = [...updated[activeFiling]];
    if (value === '' || value === 'Infinity' || value === '__INF__') {
      arr[idx] = { ...arr[idx], [field]: '__INF__' };
    } else {
      arr[idx] = { ...arr[idx], [field]: field === 'rate' ? parseFloat(value) || 0 : parseFloat(value) || 0 };
    }
    updated[activeFiling] = arr;
    setDraft(updated);
    setHasChanges(true);
  };

  const addBracket = () => {
    const updated = { ...draft };
    const arr = [...updated[activeFiling]];
    const lastMax = arr.length > 0 ? (arr[arr.length - 1].max === '__INF__' ? 999999 : arr[arr.length - 1].max) : 0;
    arr.push({ min: lastMax, max: '__INF__', rate: 0 });
    updated[activeFiling] = arr;
    setDraft(updated);
    setHasChanges(true);
  };

  const removeBracket = (idx) => {
    const updated = { ...draft };
    updated[activeFiling] = updated[activeFiling].filter((_, i) => i !== idx);
    setDraft(updated);
    setHasChanges(true);
  };

  const save = () => {
    const parsed = JSON.parse(JSON.stringify(draft), (k, v) => v === '__INF__' ? Infinity : v);
    updateFederalBrackets(parsed);
    setHasChanges(false);
    addToast('Federal tax brackets saved');
  };

  const cancel = () => {
    setDraft(JSON.parse(JSON.stringify(config.federalBrackets, (k, v) => v === Infinity ? '__INF__' : v)));
    setHasChanges(false);
  };

  const displayVal = (v) => v === '__INF__' ? '∞' : v;
  const editVal = (v) => v === '__INF__' ? '' : v;

  return (
    <div data-testid="federal-tax-editor" style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h3 style={{ ...S.heading, margin: 0 }}>Federal Tax Brackets</h3>
          {hasChanges && <span style={{ fontSize: 9, color: C.orange, fontWeight: 700, background: C.orangeLight, padding: '2px 8px', borderRadius: 4 }}>Unsaved Changes</span>}
        </div>
      </div>

      {/* Filing status tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        {Object.entries(filingLabels).map(([key, label]) => (
          <button
            key={key}
            data-testid={`filing-tab-${key}`}
            onClick={() => setActiveFiling(key)}
            style={{
              padding: '7px 14px',
              borderRadius: 6,
              border: activeFiling === key ? `2px solid ${C.accent}` : `1px solid ${C.border}`,
              background: activeFiling === key ? C.accentBg : C.card,
              color: activeFiling === key ? C.accent : C.textMid,
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {filingNotes[activeFiling] && (
        <div style={{ padding: '8px 12px', background: C.blueLight, borderRadius: 6, fontSize: 10, color: C.blue, marginBottom: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="information-circle" style={{ fontSize: '16px', flexShrink: 0 }} />
          {filingNotes[activeFiling]}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={S.th}>Min ($)</th>
            <th style={S.th}>Max ($)</th>
            <th style={S.th}>Rate (%)</th>
            <th style={{ ...S.th, width: 60, textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {brackets.map((b, idx) => (
            <tr key={idx} style={{ background: idx % 2 === 0 ? 'transparent' : C.bg }}>
              <td style={S.td}>
                <input
                  type="number"
                  value={b.min ?? ''}
                  onChange={(e) => updateBracket(idx, 'min', e.target.value)}
                  style={{ ...S.input, fontSize: 11, padding: '4px 8px', width: 120 }}
                />
              </td>
              <td style={S.td}>
                {b.max === '__INF__' ? (
                  <span style={{ ...S.mono, fontSize: 14, color: C.textLight }}>∞</span>
                ) : (
                  <input
                    type="number"
                    value={b.max === '__INF__' ? '' : (b.max ?? '')}
                    onChange={(e) => updateBracket(idx, 'max', e.target.value || 'Infinity')}
                    placeholder="∞"
                    style={{ ...S.input, fontSize: 11, padding: '4px 8px', width: 120 }}
                  />
                )}
              </td>
              <td style={S.td}>
                <input
                  type="number"
                  value={((b.rate || 0) * 100).toFixed(1)}
                  onChange={(e) => updateBracket(idx, 'rate', parseFloat(e.target.value) / 100)}
                  step="0.1"
                  style={{ ...S.input, fontSize: 11, padding: '4px 8px', width: 80 }}
                />
              </td>
              <td style={{ ...S.td, textAlign: 'center' }}>
                <button onClick={() => removeBracket(idx)} style={{ padding: '4px 8px', background: C.redLight, color: C.red, borderRadius: 4, border: 'none', fontSize: 10, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Icon name="trash-outline" style={{ fontSize: '14px' }} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'space-between' }}>
        <button 
          onClick={addBracket} 
          style={{
            ...S.buttonSecondary,
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }}
        >
          + Add Bracket
        </button>
        {hasChanges && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button 
              onClick={cancel} 
              style={S.buttonSecondary}
            >
              Cancel
            </button>
            <button 
              data-testid="save-federal-tax-btn" 
              onClick={save} 
              style={S.buttonPrimary}
            >
              Save Changes
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
